import React, { useState } from 'react'
import PropTypes from 'prop-types'
import ImmutablePropTypes from 'react-immutable-proptypes'
import map from 'lodash/map'
import { useDispatch, useSelector } from 'react-redux'
import MuiButton from 'gui/components/common/MuiButton'
import {
  Container,
  Grid,
  Box,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Typography,
  List,
  ListItem,
  ListItemText,
  Chip,
} from '@mui/material'
import ExpandMoreIcon from '@mui/icons-material/ExpandMore'
import ErrorIcon from '@mui/icons-material/Error'
import { getAuthPermissions, PERM_DELETE_CONTENT, PERM_UPDATE_CONTENT } from 'services/auth'
import { CONTENT_STORE_KEY } from 'services/content'
import { deleteContent as deleteContentAction } from 'services/content/redux/actions'
import { renderModal as renderModalAction } from 'services/modal/redux/actions'
import { CONFIRM_MODAL } from 'services/modal'
import connectHistory from 'gui/components/router/connect'
import { selectAuth } from 'services/auth/redux/selectors'

/**
 * Get the save button text
 * @param {Object} options The params
 * @param {Boolean} options.processing True when the content view is processing
 * @param {Boolean} options.isModified True when the content view is modified
 * @returns {String} The button text
 */
const getSaveButtonText = (options = {}) => {
  const { processing } = options
  if (processing) {
    return 'Saving...'
  }
  return 'Save'
}

/**
 * Format field name for display
 * @param {string} fieldName - The raw field name
 * @returns {string} The formatted field name
 */
const formatFieldName = (fieldName) =>
  // Convert camelCase to Title Case
  fieldName
    .replace(/([A-Z])/g, ' $1')
    .replace(/^./, (str) => str.toUpperCase())
    .replace(/\b\w/g, (l) => l.toUpperCase())

const ContentActions = ({
  errors,
  storeKey,
  history,
  isSubmitting,
  processing,
  content,
  isValid,
  isDirty,
  onErrorClick,
}) => {
  const auth = useSelector(selectAuth)
  const dispatch = useDispatch()
  const [errorAccordionExpanded, setErrorAccordionExpanded] = useState(true)

  const authPermissions = getAuthPermissions(auth)
  const canDelete = authPermissions.includes(PERM_DELETE_CONTENT)
  const isViewOnlyMode = !authPermissions.includes(PERM_UPDATE_CONTENT)

  // Check if there are any errors
  const hasErrors = errors && Object.keys(errors).length > 0

  const handleDelete = async () => {
    dispatch(
      deleteContentAction({
        storeKey,
        contentId: content.get('id'),
      }),
    )
    history.push('/content')
  }

  const openConfirmModal = () => {
    dispatch(
      renderModalAction(CONFIRM_MODAL, {
        innerClass: 'modal__confirm',
        backgroundDismiss: true,
        onConfirm: handleDelete,
        header: 'Delete Content',
        confirmText: `Are you sure you want to do delete "${content.get('title')}"`,
      }),
    )
  }

  const handleCancel = () => {
    history.push('/content')
  }

  const handleErrorClick = (fieldName) => {
    if (onErrorClick) {
      onErrorClick(fieldName)
    }
  }

  return (
    <>
      {/* Save and Cancel Buttons - Hover when there are changes */}
      <Box
        className="content-detail__action-buttons"
        sx={{
          position: 'fixed',
          bottom: isDirty ? '20px' : '-100px',
          right: '20px',
          display: 'flex',
          flexDirection: 'column',
          gap: '10px',
          zIndex: 1000,
          background: 'rgba(255, 255, 255, 0.95)',
          backdropFilter: 'blur(8px)',
          padding: '12px 16px',
          borderRadius: '8px',
          boxShadow: '0 4px 16px rgba(0, 0, 0, 0.15)',
          transition: 'all 0.3s ease',
          opacity: isDirty ? 1 : 0,
          pointerEvents: isDirty ? 'auto' : 'none',
          minWidth: '200px',
        }}
      >
        {/* Error Accordion */}
        {hasErrors && (
          <Accordion
            expanded={errorAccordionExpanded}
            onChange={() => setErrorAccordionExpanded(!errorAccordionExpanded)}
            sx={{
              background: 'rgba(244, 67, 54, 0.1)',
              border: '1px solid rgba(244, 67, 54, 0.3)',
              boxShadow: 'none',
              '&:before': {
                display: 'none',
              },
              '& .MuiPaper-root': {
                boxShadow: 'none',
              },
              '& .MuiAccordionSummary-root': {
                boxShadow: 'none',
              },
              '& .MuiAccordionDetails-root': {
                boxShadow: 'none',
              },
            }}
          >
            <AccordionSummary
              expandIcon={<ExpandMoreIcon />}
              sx={{
                minHeight: '40px',
                '& .MuiAccordionSummary-content': {
                  margin: '8px 0',
                },
              }}
            >
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <ErrorIcon color="error" fontSize="small" />
                <Typography variant="body2" color="error" fontWeight="medium">
                  {Object.keys(errors).length} Error{Object.keys(errors).length !== 1 ? 's' : ''}
                </Typography>
              </Box>
            </AccordionSummary>
            <AccordionDetails sx={{ padding: '8px 16px 16px' }}>
              <List dense sx={{ padding: 0 }}>
                {map(errors, (error, fieldName) => (
                  <ListItem
                    key={fieldName}
                    button
                    onClick={() => handleErrorClick(fieldName)}
                    sx={{
                      padding: '4px 8px',
                      borderRadius: '4px',
                      marginBottom: '4px',
                      '&:hover': {
                        background: 'rgba(244, 67, 54, 0.1)',
                      },
                    }}
                  >
                    <ListItemText
                      primary={
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <Chip
                            label={formatFieldName(fieldName)}
                            size="small"
                            color="error"
                            variant="outlined"
                            sx={{ fontSize: '0.75rem' }}
                          />
                          <Typography variant="body2" color="error">
                            {error}
                          </Typography>
                        </Box>
                      }
                    />
                  </ListItem>
                ))}
              </List>
            </AccordionDetails>
          </Accordion>
        )}

        {/* Action Buttons */}
        <Box sx={{ display: 'flex', gap: '10px', marginTop: '8px', marginLeft: '8px' }}>
          <MuiButton
            variant="contained"
            type="submit"
            disabled={isViewOnlyMode || processing || isSubmitting || !(isValid && isDirty)}
          >
            {getSaveButtonText({ processing })}
          </MuiButton>
          <MuiButton
            variant="outlined"
            onClick={handleCancel}
            disabled={isViewOnlyMode || processing}
          >
            Cancel
          </MuiButton>
        </Box>
      </Box>

      {/* Delete Button - Always visible at bottom */}
      <Container
        className="content-detail__section content-actions-delete-section"
        maxWidth={false}
      >
        <Grid container spacing={2}>
          <Grid item xs={12}>
            <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
              <MuiButton
                variant="contained"
                color="error"
                onClick={openConfirmModal}
                disabled={isViewOnlyMode || processing || !canDelete}
                sx={{ minWidth: '120px', height: '36px', fontSize: '14px' }}
              >
                Delete
              </MuiButton>
            </Box>
          </Grid>
        </Grid>
      </Container>
    </>
  )
}

ContentActions.propTypes = {
  content: ImmutablePropTypes.map.isRequired,
  errors: PropTypes.object,
  isValid: PropTypes.bool.isRequired,
  isDirty: PropTypes.bool.isRequired,
  isSubmitting: PropTypes.bool,
  processing: PropTypes.bool.isRequired,
  storeKey: PropTypes.string.isRequired,
  onErrorClick: PropTypes.func,
}

ContentActions.defaultProps = {
  storeKey: CONTENT_STORE_KEY,
  errors: {},
  isSubmitting: false,
  onErrorClick: null,
}

export default connectHistory()(ContentActions)
